import React from "react";

/**
 * Reusable Loader component (single file) for:
 * - Spinner loaders (buttons / inline / full section)
 * - Skeleton loaders (cards / tables / text)
 *
 * Usage:
 *  <Loader /> // spinner (default)
 *  <Loader type="spinner" size="sm" />
 *  <Loader type="skeleton" height={14} width="70%" />
 *  <Loader.Table rows={6} />
 *  <Loader.SummaryCards />
 */
export default function Loader({
  type = "spinner",
  size = "md",
  inline = false,
  width,
  height,
  style,
  className = "",
  label,
}) {
  if (type === "skeleton") {
    return (
      <div
        className={`skeleton ${className}`}
        style={{
          width: width ?? "100%",
          height: height ?? 14,
          borderRadius: 10,
          ...style,
        }}
        aria-label={label ?? "Loading"}
      />
    );
  }

  const sizePx = size === "sm" ? 14 : size === "lg" ? 22 : 18;

  return (
    <span
      className={`loader-spinner ${inline ? "inline" : ""} ${className}`}
      style={{ width: sizePx, height: sizePx, ...style }}
      aria-label={label ?? "Loading"}
    />
  );
}

/* ===== Pre-baked skeletons (still in same file) ===== */

Loader.TableRows = function TableRowsSkeleton({ rows = 6 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="tasks-row" style={{ cursor: "default" }}>
          <Loader type="skeleton" height={14} width="70%" />
          <div className="assignee">
            <div className="avatar" style={{ opacity: 0.55 }}>
              <Loader
                type="skeleton"
                height={12}
                width={12}
                style={{ borderRadius: 999 }}
              />
            </div>
            <Loader type="skeleton" height={14} width="55%" />
          </div>
          <Loader type="skeleton" height={14} width="60%" />
          <Loader
            type="skeleton"
            height={22}
            width="60%"
            style={{ borderRadius: 999 }}
          />
        </div>
      ))}
    </>
  );
};

Loader.Table = function TableSkeleton({ rows = 6 }) {
  return (
    <div className="glass-card" style={{ padding: 0 }}>
      <div className="tasks-table">
        <div className="tasks-row header">
          <div>Task</div>
          <div>Assignee</div>
          <div>Due Date</div>
          <div>Status</div>
        </div>
        <Loader.TableRows rows={rows} />
      </div>
    </div>
  );
};

Loader.SummaryCards = function SummaryCardsSkeleton() {
  return (
    <div className="summary-grid">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="glass-card" style={{ padding: 20, minWidth: 160 }}>
          <Loader type="skeleton" height={12} width="55%" style={{ marginBottom: 10 }} />
          <Loader type="skeleton" height={28} width="40%" />
        </div>
      ))}
    </div>
  );
};
